import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useStaticQuery, graphql } from 'gatsby'
import { injectIntl, Link } from 'gatsby-plugin-react-intl'
import Headroom from 'react-headroom'
import { ContentWrapper } from '@components/ui/wrappers/Wrappers'
import { GlobalPadding } from '@components/ui/wrappers/Wrappers'
import { color, breakpoint, transition, opacity } from '@configs/utilities'
import { gsap } from 'gsap'
import clickOutside from '@configs/clickOutside'
import NavItemsDesktop from './NavItemsDesktop'
import NavItemsMobile from './NavItemsMobile'
import NavbarHamburger from './NavbarHamburger'
import NavbarButtons from './NavbarButtons'
import logo from '@assets/images/navbar/ib-logo.svg'
import getGlobalDocument from '@configs/getGlobalDocument'
import LanguageSelector from '@components/common/LanguageSelector'

const Navbar = ({ intl }) => {
  const data = useStaticQuery(graphql`
    query {
      allNavbarJson {
        edges {
          node {
            path
            text
            megaMenu
            submenu {
              path
              text
              borderBottom
            }
          }
        }
      }
    }
  `)

  const [isOpen, navbarSwitch] = useState(false)

  let mobileMenu = useRef(null)
  let widthOn = `-100vw`
  let widthOff = `0vw`

  const toggleNav = () => {
    navbarSwitch((isOpen) => !isOpen)
    navbarMenuOnOff()
  }

  const navbarMenuOnOff = () => {
    let overlay = getGlobalDocument().getElementsByClassName('overlay')

    if (!isOpen) {
      gsap.to(mobileMenu.current, { duration: 0.2, x: widthOn, opacity: 1 })
      gsap.to(overlay, { duration: 0.1, opacity: 1, display: 'block' })
    } else {
      gsap.to(mobileMenu.current, { duration: 0.2, x: widthOff, opacity: 0 })
      gsap.to(overlay, { duration: 0.1, opacity: 0, display: 'none' })
    }
  }

  const clickOutsideHandler = () => {
    let overlay = getGlobalDocument().getElementsByClassName('overlay')

    if (isOpen) {
      gsap.to(mobileMenu.current, { duration: 0.2, x: widthOff })
      gsap.to(overlay, { duration: 0, opacity: 0, display: 'none' })
      navbarSwitch(!isOpen)
    }
  }

  clickOutside(mobileMenu, clickOutsideHandler)

  useEffect(() => {
    let documentStyle = getGlobalDocument().getElementsByTagName('html')[0]

    isOpen ? (documentStyle.style.overflow = 'hidden') : (documentStyle.style.overflow = 'auto')
  }, [isOpen])

  return (
    <Headroom style={{ zIndex: 9 }}>
      <NavWrapper>
        <ContentWrapper>
          <GlobalPadding>
            <NavContent>
              <NavLogo onClick={isOpen ? toggleNav : null}>
                <Link to="/">
                  <img src={logo} alt="IdeaBuddy Logo" />
                </Link>
              </NavLogo>
              <NavItemsDesktop data={data.allNavbarJson.edges} intl={intl} />
              <ButtonWrapper>
                <LanguageSelectorWrapperDesktop>
                  <LanguageSelector />
                </LanguageSelectorWrapperDesktop>
                <NavbarButtons />
              </ButtonWrapper>
              <HamburgerLangWrapper>
                <LanguageSelectorWrapperMobile>
                  <LanguageSelector mobile={true} />
                </LanguageSelectorWrapperMobile>
                <NavbarHamburger toggleNav={toggleNav} isOpen={isOpen} />
              </HamburgerLangWrapper>
              <NavItemsMobileWrapper ref={mobileMenu}>
                <NavItemsMobile isOpen={isOpen} data={data.allNavbarJson.edges} intl={intl} />
              </NavItemsMobileWrapper>
            </NavContent>
          </GlobalPadding>
        </ContentWrapper>
      </NavWrapper>
    </Headroom>
  )
}

export default injectIntl(Navbar)

const NavItemsMobileWrapper = styled.div`
  position: absolute;
  box-shadow: 0 8px 8px rgba(0, 0, 0, 0.05);
  top: 55px;
  right: -100%;
  width: 100%;
  padding: 30px;
  background: ${color.white};
  transform: ${transition.default};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-height: calc(100vh - 60px);
  overflow-y: auto;

  @media screen and (min-width: ${breakpoint.md}) {
    top: 85px;
  }
`

const NavWrapper = styled.div`
  width: 100%;
  height: 100%;
  background: ${color.white};
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.05);
`

const NavContent = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${color.white};
  padding: 7px 0;

  @media screen and (min-width: ${breakpoint.md}) {
    padding: 12px 0;
  }

  @media screen and (min-width: ${breakpoint.lg}) {
    align-items: flex-end;
  }
`

const NavLogo = styled.div`
  margin-top: 10px;
  width: 150px;
  transition: all 0.3s ease-in-out;
  margin-bottom: 10px;

  @media screen and (min-width: ${breakpoint.lg}) {
    margin-right: 20px;
    width: 120px;

    &:hover {
      opacity: ${opacity.default};
    }
  }

  @media screen and (min-width: ${breakpoint.xl}) {
    width: 150px;
    margin-right: 40px;
    margin-bottom: 7.5px;
  }
`

const HamburgerLangWrapper = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 10px;
`

const LanguageSelectorWrapperDesktop = styled.div`
  display: none;
  @media screen and (min-width: ${breakpoint.lg}) {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-right: 20px;
    min-width: 150px;
  }
`

const LanguageSelectorWrapperMobile = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media screen and (min-width: ${breakpoint.lg}) {
    display: none;
  }
`

const ButtonWrapper = styled.div`
  display: none;
  @media screen and (min-width: ${breakpoint.lg}) {
    display: flex;
    margin-bottom: 5px;
  }
`
